import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { View } from 'react-native'
import { StyleSheet } from 'react-native-unistyles'
import z from 'zod'

import FormFieldSelect from '@/components/form/FormFieldSelect'
import FormFieldText from '@/components/form/FormFieldText'
import UniButton from '@/components/unistyles/UniButton'
import DocumentType from '@/enums/documentType'
import trpc from '@/utils/trpc'
import { DocumentSelectorTypes as DocumentTypeSelectors } from '../../../../constants'

const formSchema = z.object({
  detailShort: z.string().optional().nullable(),
  type: z.nativeEnum(DocumentType),
})

type FormData = z.infer<typeof formSchema>

interface Props {
  documentId: string
  onSubmit: () => void
}

const DocumentEdit = ({ documentId, onSubmit }: Props) => {
  const documentQuery = trpc.foster.documents.byId.useQuery(
    {
      id: documentId,
    },
    {
      enabled: true,
    }
  )

  const documentMutationQuery = trpc.foster.documents.update.useMutation()

  const defaultValues = useMemo(() => {
    return {
      detailShort: documentQuery.data?.detailShort,
      type: documentQuery.data?.type as DocumentType,
    }
  }, [documentQuery.data])

  const documentForm = useForm<FormData>({
    mode: 'onBlur',
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  useEffect(() => {
    if (documentQuery.data) {
      documentForm.reset({
        detailShort: documentQuery.data.detailShort,
        type: documentQuery.data.type as DocumentType,
      })
    }
  }, [documentForm, documentQuery.data])

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
  } = documentForm

  const onInnerSubmit = handleSubmit(async (data) => {
    await documentMutationQuery.mutateAsync(
      {
        ...data,
        id: documentId,
      },
      {
        onSuccess: (updatedData) => {
          reset({
            ...updatedData,
            type: updatedData.type as DocumentType,
          })
          documentQuery.refetch()
        },
      }
    )

    documentQuery.refetch()

    onSubmit()
  })

  return (
    <View style={styles.root}>
      <View style={styles.formContainer}>
        <FormFieldSelect
          control={control}
          data={DocumentTypeSelectors}
          errors={errors}
          fieldName="type"
          label="Select Type"
          required={false}
        />
        <FormFieldText
          control={control}
          errors={errors}
          fieldName="detailShort"
          label="Short Description"
          required={false}
        />
        <UniButton
          disabled={!isDirty || documentMutationQuery.isPending}
          loading={documentMutationQuery.isPending}
          mode="contained"
          onPress={onInnerSubmit}
          style={{ marginTop: 5 }}
        >
          Update
        </UniButton>
      </View>
    </View>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    formContainer: {
      alignSelf: {
        md: undefined,
        lg: 'center',
      },
      flex: {
        md: undefined,
        lg: 0.55,
      },
      flexDirection: 'column',
      gap: theme.tokens.spacing[2],
    },
    root: {
      flex: 1,
      flexDirection: {
        md: 'column',
        lg: 'row',
      },
      gap: theme.tokens.spacing[4],
    },
  }
})

export default DocumentEdit
